/**
 * Locks the host form ScrollView while a native record-table drag is active
 * (#999 / #1401 — Devices + step lists on iOS/Android).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

interface RecordTableDragScrollLockValue {
  /** True while any native table row drag is in progress. */
  isTableDragActive: boolean;
  beginTableDrag: () => void;
  endTableDrag: () => void;
}

const RecordTableDragScrollLockContext =
  createContext<RecordTableDragScrollLockValue | null>(null);

export function RecordTableDragScrollLockProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [isTableDragActive, setActive] = useState(false);
  const depthRef = useRef(0);

  const beginTableDrag = useCallback(() => {
    depthRef.current += 1;
    setActive(true);
  }, []);

  const endTableDrag = useCallback(() => {
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) {
      setActive(false);
    }
  }, []);

  const value = useMemo(
    () => ({ isTableDragActive, beginTableDrag, endTableDrag }),
    [isTableDragActive, beginTableDrag, endTableDrag],
  );

  return (
    <RecordTableDragScrollLockContext.Provider value={value}>
      {children}
    </RecordTableDragScrollLockContext.Provider>
  );
}

export function useRecordTableDragScrollLock(): RecordTableDragScrollLockValue {
  const ctx = useContext(RecordTableDragScrollLockContext);
  return (
    ctx ?? {
      isTableDragActive: false,
      beginTableDrag: () => undefined,
      endTableDrag: () => undefined,
    }
  );
}
